import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/components/common";
import { jobsService } from "@/api/services/jobs.service";
import { contactsService } from "@/api/services/contacts.service";
import { formsService } from "@/api/services/forms.service";
import { FormStatus } from "@/types/form.types";
import { parseBuildingPermitFromNotes, } from "@/utils/buildingPermitNotes";
import { selectCurrentBuildingPermitForm } from "@/utils/buildingPermitFormUtils";
import { buildFormPrintHtml, notesToFormDataOverrides, } from "@/utils/formPrintHtml";
import { FormDisplay } from "@/features/admin/forms/components/FormDisplay";
import { mergeFormData } from "@/features/admin/forms/FormDetail";
import styles from "./JobFormView.module.css";
const PrintIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("polyline", { points: "6 9 6 2 18 2 18 9" }), _jsx("path", { d: "M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" }), _jsx("rect", { x: "6", y: "14", width: "12", height: "8" })] }));
const DownloadIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] }));
const ArrowUpIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "12", y1: "19", x2: "12", y2: "5" }), _jsx("polyline", { points: "5 12 12 5 19 12" })] }));
const BackIcon = () => (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "19", y1: "12", x2: "5", y2: "12" }), _jsx("polyline", { points: "12 19 5 12 12 5" })] }));
const EMPTY_FORM_DATA = {
    jobNumber: "",
    applicant: {
        contactName: "",
        email: "",
        companyName: "",
        phone: "",
        address: "",
        documentAddress: "",
    },
    owner: {
        name: "",
        email: "",
        companyName: "",
        phone: "",
        address: "",
        acnArbn: "",
    },
    tenant: {
        contactPerson: "",
        email: "",
        companyName: "",
        phone: "",
        address: "",
    },
    builder: {
        contactPerson: "",
        email: "",
        companyName: "",
        phone: "",
        address: "",
    },
    lesseeResponsible: false,
    selectedSurveyor: "",
};
export const JobFormView = () => {
    const navigate = useNavigate();
    const { jobId } = useParams();
    const { showToast } = useToast();
    const [formData, setFormData] = useState(EMPTY_FORM_DATA);
    const [job, setJob] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasNotes, setHasNotes] = useState(false);
    const [surveyorCompanyName, setSurveyorCompanyName] = useState(null);
    const [resolvedForm, setResolvedForm] = useState(null);
    const [isMissingCompany, setIsMissingCompany] = useState(false);
    const [isFormResolveComplete, setIsFormResolveComplete] = useState(false);
    const fetchJobAndParseNotes = useCallback(async () => {
        if (!jobId) {
            setIsLoading(false);
            setFormData(EMPTY_FORM_DATA);
            setJob(null);
            setResolvedForm(null);
            setSurveyorCompanyName(null);
            setIsFormResolveComplete(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        setResolvedForm(null);
        setSurveyorCompanyName(null);
        setIsFormResolveComplete(false);
        try {
            const response = await jobsService.getJob(jobId);
            const jobData = response.data;
            if (!jobData) {
                setFormData(EMPTY_FORM_DATA);
                setJob(null);
                setHasNotes(false);
                return;
            }
            setJob(jobData);
            const parsed = parseBuildingPermitFromNotes(jobData.notes, jobData.jobNumber ?? jobId);
            if (parsed) {
                setFormData(parsed);
                setHasNotes(true);
            }
            else {
                setFormData({
                    ...EMPTY_FORM_DATA,
                    jobNumber: jobData.jobNumber ?? jobId,
                });
                setHasNotes(false);
            }
        }
        catch (err) {
            console.error("Failed to fetch job for form:", err);
            setError("Failed to load job. Please try again.");
            setFormData(EMPTY_FORM_DATA);
            setJob(null);
        }
        finally {
            setIsLoading(false);
        }
    }, [jobId]);
    useEffect(() => {
        fetchJobAndParseNotes();
    }, [fetchJobAndParseNotes]);
    useEffect(() => {
        const surveyorId = job?.buildingSurveyorId;
        setSurveyorCompanyName(null);
        setResolvedForm(null);
        if (!surveyorId) {
            setIsMissingCompany(false);
            return;
        }
        setIsMissingCompany(false);
        let cancelled = false;
        const fetchSurveyorCompany = async () => {
            try {
                const response = await contactsService.getContact(surveyorId);
                if (cancelled)
                    return;
                const companyName = response.data?.companyName?.trim() || null;
                setSurveyorCompanyName(companyName);
                setIsMissingCompany(!companyName);
            }
            catch {
                if (!cancelled) {
                    setSurveyorCompanyName(null);
                    setIsMissingCompany(true);
                }
            }
        };
        void fetchSurveyorCompany();
        return () => {
            cancelled = true;
        };
    }, [job?.buildingSurveyorId]);
    useEffect(() => {
        const surveyorId = job?.buildingSurveyorId;
        setResolvedForm(null);
        setIsFormResolveComplete(false);
        if (!surveyorId) {
            setIsFormResolveComplete(true);
            return;
        }
        let cancelled = false;
        const resolveForm = async () => {
            try {
                const contactRes = await contactsService.getContact(surveyorId);
                if (cancelled)
                    return;
                const companyName = contactRes.data?.companyName?.trim();
                if (!companyName) {
                    if (!cancelled) {
                        setResolvedForm(null);
                        setIsFormResolveComplete(true);
                    }
                    return;
                }
                const res = await formsService.getFormsByCompanyName(companyName, 1, 20);
                if (cancelled)
                    return;
                const forms = res.data?.data ?? [];
                const selected = selectCurrentBuildingPermitForm(forms);
                if (!cancelled) {
                    setResolvedForm(selected ?? null);
                    setIsFormResolveComplete(true);
                }
            }
            catch {
                if (!cancelled) {
                    setResolvedForm(null);
                    setIsFormResolveComplete(true);
                }
            }
        };
        void resolveForm();
        return () => {
            cancelled = true;
        };
    }, [job?.buildingSurveyorId]);
    const fallbackForm = useMemo(() => ({
        id: "fallback",
        name: "APPLICATION AND AGREEMENT FORM FOR A BUILDING PERMIT - COMMERCIAL",
        templateId: "fallback",
        templateName: "Building Permit Form",
        templateType: "Building Permit",
        companyName: null,
        siteId: null,
        jobId: jobId ?? null,
        contactId: null,
        data: null,
        status: FormStatus.DRAFT,
        submittedById: null,
        submittedByName: null,
        reviewedById: null,
        reviewedByName: null,
        notes: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        submittedAt: null,
        reviewedAt: null,
    }), [jobId]);
    const isFormResolving = (job?.buildingSurveyorId != null) && !isFormResolveComplete;
    const mergedFormDataForDisplay = useMemo(() => {
        const overrides = hasNotes ? notesToFormDataOverrides(formData) : {};
        const recipientValue = overrides.recipient ||
            formData.selectedSurveyor ||
            job?.buildingSurveyorName ||
            surveyorCompanyName ||
            "";
        if (resolvedForm) {
            const templateData = (resolvedForm.data ?? {});
            return mergeFormData({
                ...templateData,
                ...overrides,
                recipient: recipientValue || templateData.recipient || "",
            });
        }
        if (hasNotes) {
            return mergeFormData({
                ...overrides,
                recipient: recipientValue,
            });
        }
        return null;
    }, [resolvedForm, formData, hasNotes, job, surveyorCompanyName]);
    const handlePrint = () => {
        if (isMissingCompany) {
            showToast("Please fill in the company for this building surveyor.", "error");
            return;
        }
        window.print();
    };
    const handleDownload = () => {
        if (!jobId) {
            showToast("No job selected. Please go back and try again.", "error");
            return;
        }
        if (isMissingCompany) {
            showToast("Please fill in the company for this building surveyor.", "error");
            return;
        }
        showToast("Preparing form for download...", "info");
        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            showToast("Please allow pop-ups to download forms", "error");
            return;
        }
        const dataOverrides = hasNotes ? notesToFormDataOverrides(formData) : {};
        const formToUse = resolvedForm ?? fallbackForm;
        const htmlContent = buildFormPrintHtml(formToUse, {
            title: `Building Permit Application - Job ${job?.jobNumber ?? jobId}`,
            autoPrint: true,
            dataOverrides,
        });
        printWindow.document.write(htmlContent);
        printWindow.document.close();
    };
    const handleBackToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleBack = () => {
        navigate(-1);
    };
    if (isLoading || isFormResolving) {
        return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.topHeader, children: [_jsx("button", { className: styles.backButton, onClick: handleBack, children: _jsx(BackIcon, {}) }), _jsx("h1", { className: styles.topTitle, children: "APPLICATION AND AGREEMENT FORM FOR A BUILDING PERMIT" })] }), _jsx("div", { className: styles.formContent, children: _jsxs("div", { className: styles.loadingContainer, children: [_jsx("div", { className: styles.loadingSpinner }), _jsx("p", { className: styles.loadingMessage, children: "Preparing your form..." })] }) })] }));
    }
    if (error) {
        return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.topHeader, children: [_jsx("button", { className: styles.backButton, onClick: handleBack, children: _jsx(BackIcon, {}) }), _jsx("h1", { className: styles.topTitle, children: "APPLICATION AND AGREEMENT FORM FOR A BUILDING PERMIT" })] }), _jsxs("div", { className: styles.formContent, children: [_jsx("p", { style: { color: "var(--color-rust)" }, children: error }), _jsx("button", { type: "button", onClick: fetchJobAndParseNotes, children: "Retry" })] })] }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.topHeader, children: [_jsx("button", { className: styles.backButton, onClick: handleBack, children: _jsx(BackIcon, {}) }), _jsx("h1", { className: styles.topTitle, children: "APPLICATION AND AGREEMENT FORM FOR A BUILDING PERMIT" }), _jsxs("div", { className: styles.topActions, children: [_jsxs("button", { className: styles.printButton, onClick: handlePrint, children: [_jsx(PrintIcon, {}), "PRINT FORM"] }), _jsxs("button", { className: styles.downloadButton, onClick: handleDownload, children: [_jsx(DownloadIcon, {}), "DOWNLOAD FORM"] })] })] }), _jsx("div", { className: styles.formContent, children: isMissingCompany && job?.buildingSurveyorId ? (_jsx("div", { className: styles.section, style: {
                        padding: "var(--space-md)",
                        background: "var(--color-misty-rose)",
                        borderRadius: "var(--radius-md)",
                    }, children: _jsx("p", { children: "Please fill in the company for this building surveyor to view the form." }) })) : mergedFormDataForDisplay ? (_jsx(FormDisplay, { form: resolvedForm ?? fallbackForm, formData: mergedFormDataForDisplay, showHeader: true })) : (_jsx("div", { className: styles.section, style: {
                        padding: "var(--space-md)",
                        background: "var(--color-misty-rose)",
                        borderRadius: "var(--radius-md)",
                    }, children: _jsx("p", { children: "No Building Permit Application data for this job." }) })) }), _jsxs("div", { className: styles.bottomActions, children: [_jsx("button", { className: styles.backButtonBottom, onClick: handleBack, children: _jsx(BackIcon, {}) }), _jsxs("div", { className: styles.bottomButtons, children: [_jsxs("button", { className: styles.printButtonBottom, onClick: handlePrint, children: [_jsx(PrintIcon, {}), "PRINT FORM"] }), _jsxs("button", { className: styles.downloadButtonBottom, onClick: handleDownload, children: [_jsx(DownloadIcon, {}), "DOWNLOAD FORM"] }), _jsxs("button", { className: styles.backToTopButton, onClick: handleBackToTop, children: [_jsx(ArrowUpIcon, {}), "BACK TO TOP"] })] })] })] }));
};
